import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SourcedItem, SourcingOutcomeInformation, VendorsList, RFQSummaryRole } from 'src/app/shared/models/app-models/SourcingOutcome.model';
import { Store } from '@ngrx/store';
import {
  ExtendedKeycloakUser,
  ExtendedMaterialSubGroup,
  ExtendedOEM,
  StoreDropdowns,
  UserInformation,
} from 'src/app/shared/models/app-models';
import Papa from 'papaparse';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { DatePipe } from '@angular/common';
import { StatusData } from 'src/app/modules/rfq-summary/rfq-summary.component';
import { MatDialog } from '@angular/material/dialog';
import { RaisePoDialogComponent } from '../raise-po-dialog/raise-po-dialog.component';
import { ExcelUpdateDiologComponent } from '../excel-upload-diolog/excel-upload-diolog.component';
import moment from 'moment';
import { MPMMSInformation } from 'src/app/shared/models/app-models/MPMMSInformation.model'
import { environment } from 'src/environments/environment';
import { KeycloakService } from 'keycloak-angular';

const INITIAL_CSV_HEADER_KEYS = [
  'PurchaseItemNumber',
  'RFQItemNumber',
  'Quantity',
  'MaterialSubGroupId',
  'ItemDescription',
  'Vendor',
  'PONumber',
  'PODocStatus',
  'AwardedQuantity',
  'RFQQuantity',
  'UnitOfMeasurement',
  'BrandOEMName',
  'PartModelNumber',
  'PlannedDeliveryDate',
  'PurchaseItemId',
  'Id',
] as const;

type CsvHeaderKey = (typeof INITIAL_CSV_HEADER_KEYS)[number];

const CSV_HEADER_MAPPING: { [key in CsvHeaderKey]: string } = {
  PurchaseItemNumber: 'PurchaseItemNumber',
  RFQItemNumber: 'RFQItemNumber',
  Quantity: 'Outstanding Qty',
  MaterialSubGroupId: 'MaterialSubGroupId',
  ItemDescription: 'ItemDescription',
  Vendor: 'Awarded Vendor (populated from PO raised)',
  PONumber: 'PONumber',
  PODocStatus: 'PO Status',
  AwardedQuantity: 'AwardedQuantity (populated from PO raised)',
  RFQQuantity: 'RFQQuantity',
  UnitOfMeasurement: 'UnitOfMeasurement',
  BrandOEMName: 'BrandOEMName',
  PartModelNumber: 'PartModelNumber',
  PlannedDeliveryDate: 'PlannedDeliveryDate',
  PurchaseItemId: 'PurchaseItemId',
  Id: 'Id',
};
;

@Component({
  selector: 'app-items-list',
  templateUrl: './items-list.component.html',
  styleUrls: ['./items-list.component.scss'],
  providers: [
    DatePipe
  ],
})

export class ItemsListComponent implements OnInit, OnChanges {
  destroy$: Subject<boolean> = new Subject<boolean>();
  @Input() statusData!: StatusData;
  @Input() userRoleOnRfqSummary!: string;
  @Input() list: SourcedItem[] = [];
  @Input() isReadOnlyRFQSummary: boolean = false;
  @Output() onRaisePO: EventEmitter<any> = new EventEmitter<any>();
  @Input() mpMmsInformation: MPMMSInformation[] = []
  @Output() allItemsHavePoChange = new EventEmitter<boolean>();

  selection: SourcedItem[] = [];
  preselect: SourcedItem[] = [];
  rfqSummary!: SourcingOutcomeInformation;
  materialSubGroups: ExtendedMaterialSubGroup[] = [];
  OEMList: ExtendedOEM[] = [];
  userList: ExtendedKeycloakUser[] = [];
  loggedInUser!: UserInformation;

  groupedMpMmsData: { [key: number]: MPMMSInformation[] } = {};
  transformedList: SourcedItem[] = [];
  hasPOAccess: boolean = false;

  constructor(
    private store: Store<{ dp: StoreDropdowns, user: UserInformation, rfqSummary: SourcingOutcomeInformation }>,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private keycloakService: KeycloakService,
  ) {
    this.store
      .select('dp')
      .pipe(takeUntil(this.destroy$))
      .subscribe((dp: StoreDropdowns) => {
        this.userList = dp.UserList as ExtendedKeycloakUser[];
        this.materialSubGroups = dp.MaterialSubGroup as ExtendedMaterialSubGroup[];
        this.OEMList = dp.OEM as ExtendedOEM[];
      });

    combineLatest([this.store
      .select('user'), this.store
        .select('rfqSummary')]).pipe(takeUntil(this.destroy$)).subscribe(([user, rfqSummary]: [UserInformation, SourcingOutcomeInformation]) => {
          this.loggedInUser = user;
          this.rfqSummary = rfqSummary;
        }
        );
  }

  ngOnInit(): void {
    this.hasPOAccess = this.keycloakService.getUserRoles().some((role) => role == "MP_MMS_PO_CREATE_OWNER");
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['list'] || changes['mpMmsInformation']) {
      this.populateDataSource();
    }
  }

  populateDataSource() {
    if (this.list && this.mpMmsInformation) {
      // Flatten the nested array structure
      const flattenedMmsData = this.mpMmsInformation.flat();

      // Group MMS data by ItemNumber
      const groupedMpMmsData = flattenedMmsData.reduce((acc, mms) => {
        if (!acc[mms.CopiedFromItemNumber]) {
          acc[mms.CopiedFromItemNumber] = [];
        }
        acc[mms.CopiedFromItemNumber].push(mms);
        return acc;
      }, {} as { [key: number]: any[] });

      this.groupedMpMmsData = groupedMpMmsData;
      console.log("this.groupedMpMmsData", this.groupedMpMmsData);


      // Merge 'list' with grouped MMS data
      const mergedData = this.list.map(item => {
        let mmsInfos = groupedMpMmsData[item.PurchaseItemId] || [];

        // Format Vendor and PO Number data for display
        const vendorInfo = mmsInfos.map(mms => `${mms?.VendorName?.Name.trim() || ''}`).join(', ');
        const poNumbers = mmsInfos.map(mms => `${mms?.DocumentNumber || ''}`).join(', ');
        const awardedQty = mmsInfos.map(mms => `${mms?.Quantity || ''}`).join(', ');
        const poDocStatus = mmsInfos.map(mms => `${mms?.ProcessStatus || ''}`).join(', ');

        // Determine Quantity and AwardedQuantity
        const totalAwardedQty = mmsInfos.reduce((sum, mms) => sum + (mms.Quantity || 0), 0);

        return {
          ...item,
          Vendor: vendorInfo || '-',
          PONumber: poNumbers || '-',
          Quantity: (item?.Quantity || 0) - totalAwardedQty,
          RFQQuantity: item?.Quantity || 0,
          AwardedQuantity: awardedQty || '0',
          PODocStatus: poDocStatus || '-'
        };
      });

      // Set the merged data as the data source for the table
      this.transformedList = mergedData;

    } else {
      console.warn("List or MPMMS Information is missing.");
    }
  }

  updateAllItemsHavePO(allItemsHavePo: boolean) {
    this.allItemsHavePoChange.emit(allItemsHavePo);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  handleRaisePO() {
    // if(this.selection.length == 1) {
    //   // Awaiting for API Integration

    //   const dialogRef = this.dialog.open(RfqInfoDialogComponent, {
    //     width: '600px',
    //     data: {
    //       closeLabel: "close",
    //       title: "PO has been created on MMS",
    //       message: "Please complete the PO form on MMS. Once you have saved the PO as draft, the PO number will be reflected on the RFQ Summary.",
    //     }
    //   });

    //   // dialogRef.afterClosed().subscribe((result) => {

    //   // });
    // }

    if (this.selection.length > 0) {
      const dialogRef = this.dialog.open(RaisePoDialogComponent, {
        width: '1200px',
        data: {
          selectedRfqItems: this.selection,
          statusData: this.statusData,
          mpMmsInformation: this.mpMmsInformation,
        }
      });

      dialogRef.afterClosed().subscribe((result: VendorsList | undefined) => {
        if (result) {
          // API integration required
          const payload = {
            "RFQNumber": this.rfqSummary.RFQNumber,
            "RFQSummaryNumber": this.statusData.rfqSummaryNum,
            "MPDocumentNumber": this.rfqSummary.MPDocumentNumber,
            "VendorNumber": result.VendorNumber,
            "User": this.loggedInUser,
            items: this.selection.map((item) => {
              const { MaterialSubGroupId, ...rest } = item;
              return { ...rest }
            })
          };
          this.onRaisePO.emit(payload);
        }
      });
    }
  }

  handleSelection(list: any) {
    this.selection = list;
  }

  handleExport() {
    this.formatAndSaveCSV(this.transformedList);
  }

  handleImport() {
    const dialogRef = this.dialog.open(ExcelUpdateDiologComponent, {
      width: '680px',
      data: {
        sourcedItem: this.list
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result?.confirm) {
        const selected: SourcedItem[] = result.sourcedItem as SourcedItem[];
        this.selection = this.list.filter((item) => selected.some((il) => il.PurchaseItemNumber == item.PurchaseItemNumber));
      }
    });
  }

  isActionEnabled(): boolean {
    // only display raise po interaction when user has RO role;
    if (this.userRoleOnRfqSummary == RFQSummaryRole.RO || this.userRoleOnRfqSummary == RFQSummaryRole.Both_PE_RO) {
      // and when its either 3 (Pending Evaluation) or 4 (Evaluation in progress)
      return this.rfqSummary.DocumentStatusId == 3 || this.rfqSummary.DocumentStatusId == 4;
    } else {
      return false;
    }
  }

  onExcelUpload(event: any) {
    const file = event.target.files[0];

    if (file) {
      this.selection = [];
      Papa.parse(file, {
        header: true,
        complete: (result) => {
          const importedList: SourcedItem[] = result.data as SourcedItem[];
          this.selection = this.list.filter((item) => importedList.some((il) => il.PurchaseItemNumber == item.PurchaseItemNumber));
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
        },
      });
    }
  }

  private formatAndSaveCSV(reportData: SourcedItem[]) {
    let selectedHeaderKeys: CsvHeaderKey[] = INITIAL_CSV_HEADER_KEYS.map(
      (key) => key
    );

    const csv = Papa.unparse({
      fields: selectedHeaderKeys.map((key) => CSV_HEADER_MAPPING[key]),
      data: reportData.map((rowObj) => {

        return selectedHeaderKeys.map((key) => {
          if(key == CSV_HEADER_MAPPING.MaterialSubGroupId && rowObj.MaterialSubGroupId) {
            return this.materialSubGroups.find((material) => material.id == rowObj.MaterialSubGroupId)?.value ?? "";
          } else if (key == CSV_HEADER_MAPPING.BrandOEMName && rowObj.BrandOEMName) {
            return this.OEMList.find((item) => item.id == rowObj.BrandOEMName)?.value ?? "";
          } else if (key == CSV_HEADER_MAPPING.PlannedDeliveryDate && rowObj.PlannedDeliveryDate) {
            return this.datePipe.transform(rowObj.PlannedDeliveryDate, 'dd-MMM-yyyy') ?? "";
          } else {
            return rowObj[key] ?? null
          }
        });
      }),
    });

    // Download csv file
    var blob = new Blob([csv]);
    var a = window.document.createElement('a');
    a.href = window.URL.createObjectURL(blob);
    a.download = `${this.statusData.rfqSummaryNum}_EXPORT_${moment().format(
      'YYYYMMDDHHmmss'
    )}.csv`;
    document.body.appendChild(a);
    a.click(); // IE: "Access is denied"; see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
    document.body.removeChild(a);
  }

  public get INITIAL_CSV_HEADER_KEYS(): typeof INITIAL_CSV_HEADER_KEYS {
    return INITIAL_CSV_HEADER_KEYS;
  }

  public get CSV_HEADER_MAPPING(): typeof CSV_HEADER_MAPPING {
    return CSV_HEADER_MAPPING;
  }
}